import React from "react";
//#region types
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums, TimeReportingEnums } from "models/enums";
import { DateConstants } from "utils/constants";
import { TimeReportingDetailsViewProps } from "models/propTypes/timeReportingPropTypes";
import { TimeReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { ViewPanel, FormValidity, PageLayouts, EntityTitle, Skeletons, useFloatingMessages } from "components/common";
import { WeeklyTimeReportRow, WeeklyTimeReport } from "models/formTypes/timeReporting";
import TimeReportHelperInstance from "../../timeReportHelper";
import WeeklyFormContainer from "./forms/weeklyFormContainer";
import WeeklyViewBanner from "./weeklyViewBanner";
import WeeklyViewFooter from "./footer";
import WeekSelector from "./weekSelector";
import WeekSummary from "./weekSummary";
import { Grid } from "@mui/material";
import { useTimeReportingService } from "serviceHooks";
//#endregion
import { SalaryDeviationsService } from "services";
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const TimeReportingDetailsView: React.FC<TimeReportingDetailsViewProps> = (props) => {
  const timeReportingService = useTimeReportingService();
  const formRef = React.useRef<FormHandles>(null);

  const { enqueueMessage } = useFloatingMessages();

  const [currentMetaData, setCurrentMetaData] = React.useState<ApiModel.TimeReportMetaDataWithPeriods>(props.metaData);
  const [currentWeek, setCurrentWeek] = React.useState<WeeklyTimeReport>();
  const [updatedWeekData, setUpdatedWeekData] = React.useState<TimeReportingFormTypes.WeeklyTimeReportRow[]>([]);
  const [currentWeekLocks, setCurrentWeekLocks] = React.useState<TimeReportingFormTypes.WeekLockEntries>();
  const [currentWeekDateRange, setCurrentWeekDateRange] = React.useState<DateRange>();
  const [monthStatus, setMonthStatus] = React.useState<ApiModel.TimeReportStatus>();
  const [loading, setLoading] = React.useState(false);
  const [hasUnSavedChanges, setHasUnSavedChanges] = React.useState<boolean>(false);
  const [isCurrentWeekEmpty, setIsCurrentWeekEmpty] = React.useState<boolean>(false);

  const manipulateData = async (metaData: ApiModel.TimeReportMetaDataWithPeriods) => {
    setHasUnSavedChanges(false);
    setCurrentMetaData(metaData);
    if (metaData.weeks) {
      if (metaData.weeks.length > 1 || metaData.weeks.length === 0) {
        console.log("more than one week or empty periods");
      } else {
        const thisWeek = metaData.weeks[0];
        if (thisWeek.lastWeekInMonthInfo?.isLastWeekInMonth) {
          const weekStartDate = new Date(thisWeek.weekStart);
          const monthStartDate = DateHelper.getFirstDayOfMonth(weekStartDate);
          const monthEndDate = DateHelper.getLastDayOfMonth(weekStartDate);
          await timeReportingService
            .getDateRangeStatus(metaData.employeeId, monthStartDate, monthEndDate)
            .then((res) => setMonthStatus(res))
            .catch((errors) => {
              if (
                errors?.codes &&
                errors.codes.length === 1 &&
                errors.codes[0] === TranslationKeys.Errors.TimeReportsNotFound
              ) {
              } else {
                enqueueMessage(
                  LocalizationHelperInstance.getFormattedMessage(errors.codes.join(", ")),
                  StyleEnums.DisplayType.Danger
                );
              }
              setMonthStatus(ApiModel.TimeReportStatus.Open);
            });
        }

        const rows: WeeklyTimeReportRow[] = [];
        const headerRow = {
          name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Total),
          childName: "",
        } as any;
        const tableHeader = {
          name: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Week)} ${
            metaData.weeks[0].weekNumber
          }`,
        } as any;
        const weekLocks = {} as TimeReportingFormTypes.WeekLockEntries;
        const weekHolidays = {} as TimeReportingFormTypes.WeekDaysToggles;
        setCurrentWeekDateRange({
          start: new Date(metaData.weeks[0].weekStart),
          end: new Date(metaData.weeks[0].weekEnd),
        });
        metaData.weeks[0].timeReports?.forEach((day) => {
          const weekDayName =
            day.weekDayName !== undefined && DateConstants.FullWeekStartWithSunday[Number(day.weekDayName)];
          if (weekDayName) {
            headerRow[weekDayName] = day.totalHours;
            tableHeader[weekDayName] = new Date(day.timeLogDate).getDate();
            (weekLocks as any)[weekDayName] = day.isLocked;
            (weekHolidays as any)[weekDayName] = !day.isWorkDay;
          }

          day.projectTimes?.forEach((proj) => {
            proj.categoryTimes?.forEach((ct) => {
              const row = rows.find(
                (r) => r.projectId === proj.projectId && r.categoryId === ct.categoryId
              ) as WeeklyTimeReportRow;

              if (weekDayName) {
                if (row) {
                  (row as any)[weekDayName] = ((row as any)[weekDayName] ?? 0) + ct.hours;
                  (row.weekLocks as any)[weekDayName] = proj.isLocked && ct.isLocked;
                  (row.comments as any)[`internal${weekDayName}`] = ct.internalComment;
                  (row.comments as any)[`external${weekDayName}`] = ct.externalComment;
                } else {
                  const newWeekLock = {
                    [weekDayName]: proj.isLocked && ct.isLocked,
                  } as TimeReportingFormTypes.WeekLockEntries;
                  const newComment = {
                    [`internal${weekDayName}`]: ct.internalComment,
                    [`external${weekDayName}`]: ct.externalComment,
                  } as TimeReportingFormTypes.WeekComments;
                  rows.push({
                    type: TimeReportingEnums.TimeReportType.project,
                    name: proj.projectName ?? "",
                    categoryName: ct.categoryName,
                    categoryId: ct.categoryId,
                    [weekDayName]: ct.hours,
                    projectId: proj.projectId,
                    weekLocks: newWeekLock,
                    isInternal: proj.isInternal,
                    uniqueId: UtilsHelper.getUniqueId(),
                    comments: newComment,
                  } as WeeklyTimeReportRow);
                }
              }
            });
          });
          day.deviations?.forEach((deviation) => {
            const row = rows.find((r) => TimeReportHelperInstance.isExistingWeeklyDeviationRow(deviation, r)) as any;
            if (weekDayName) {
              if (row) {
                row[weekDayName] = (row[weekDayName] ?? 0) + deviation.hours;
                (row.weekLocks as any)[weekDayName] = deviation.isLocked;
              } else {
                const newWeekLock = { [weekDayName]: deviation.isLocked } as TimeReportingFormTypes.WeekLockEntries;
                const name = SalaryDeviationsService.getSalaryDeviationTypeText(
                  deviation.deviationType,
                  deviation.customDeviationName
                );
                rows.push({
                  type: TimeReportingEnums.TimeReportType.deviation,
                  name: name,
                  customName: deviation.customDeviationName ?? "",
                  [weekDayName]: deviation.hours,
                  deviationId: deviation.deviationId,
                  deviationType: deviation.deviationType,
                  weekLocks: newWeekLock,
                  childName: deviation.childName,
                  uniqueId: UtilsHelper.getUniqueId(),
                } as WeeklyTimeReportRow);
              }
            }
          });
        });
        setCurrentWeekLocks(weekLocks);
        setCurrentWeek({
          workedHours: metaData.weeks[0].totalHours,
          rows: rows,
          headerRow: headerRow,
          tableHeader: tableHeader,
          holidays: weekHolidays,
        } as WeeklyTimeReport);
        setUpdatedWeekData(rows);
      }
    }
  };

  React.useEffect(() => {
    setLoading(true);
    manipulateData(props.metaData).then((res) => setLoading(false));
  }, [props.metaData]);

  const loadData = (newDate: Date) => {
    if (newDate && !loading) {
      setLoading(true);
      timeReportingService
        .filter(currentMetaData.employeeId, DateHelper.getFirstDayOfWeek(newDate), DateHelper.getLastDayOfWeek(newDate))
        .then(async (res) => {
          await manipulateData(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const onCreateOrUpdateWeek = () => {
    return (
      (formRef.current && formRef.current.submit()) ||
      new Promise<void>((resolve) => resolve()).then((res) => setHasUnSavedChanges(false))
    );
  };

  const onFormUpdate = (values: TimeReportingFormTypes.WeeklyTimeReport, isValid: boolean) => {
    if (JSON.stringify(updatedWeekData) !== JSON.stringify(values.rows)) {
      if (JSON.stringify(currentWeek?.rows) === JSON.stringify(values.rows)) {
        setHasUnSavedChanges(false);
      } else {
        setHasUnSavedChanges(true);
      }
      setUpdatedWeekData(values.rows);
    }

    setIsCurrentWeekEmpty(values.rows.length === 0);
  };

  const onReloadWeek = () => {
    currentWeekDateRange?.start && loadData(currentWeekDateRange?.start);
  };

  return (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <PageLayouts.EntityViewLayout
          displayType={StyleEnums.DisplayType.Info}
          leftColumnSize="sm"
          upperLeftComponent={
            <Grid container alignItems="flex-end">
              <EntityTitle
                subTitle={currentMetaData.employeeName ?? ""}
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.TimeReporting)}
              />
            </Grid>
          }
          upperRightComponent={
            <WeekSelector
              currentWeekDateRange={currentWeekDateRange}
              onWeekToggle={loadData}
              hasUnSavedChanges={hasUnSavedChanges}
              onCreateOrUpdateWeek={onCreateOrUpdateWeek}
              employeeId={currentMetaData.employeeId}
            ></WeekSelector>
          }
          lowerLeftComponent={
            <WeekSummary formData={updatedWeekData} weekStart={currentWeekDateRange?.start}></WeekSummary>
          }
          footer={
            <WeeklyViewFooter
              isValid={isValid && !loading}
              onCreateOrUpdateWeek={onCreateOrUpdateWeek}
              weekSegments={currentMetaData.weeks && currentMetaData.weeks[0].weekSegments}
              isLastWeek={currentMetaData.weeks && currentMetaData.weeks[0].lastWeekInMonthInfo?.isLastWeekInMonth}
              employeeId={currentMetaData.employeeId}
              reloadHandler={onReloadWeek}
              monthStatus={monthStatus}
              weekLockEntries={currentWeekLocks}
              hasUnSavedChanges={hasUnSavedChanges}
            ></WeeklyViewFooter>
          }
        >
          <>
            {loading ? (
              <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
            ) : (
              <WeeklyViewBanner
                weekData={currentWeek?.rows}
                updatedWeek={updatedWeekData}
                compHours={currentMetaData.weeks && currentMetaData.weeks[0].expectedCompHours}
              />
            )}
            {loading ? (
              <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
            ) : (
              currentWeek && (
                <ViewPanel className={cssExports.timeReportingFormContainer}>
                  <WeeklyFormContainer
                    ref={formRef}
                    onFormValidationChange={onValidityChange}
                    currentMetaData={currentMetaData}
                    currentWeek={currentWeek}
                    weekLockEntries={currentWeekLocks}
                    onFormUpdate={onFormUpdate}
                    currentWeekDateRange={currentWeekDateRange}
                    isCurrentWeekEmpty={isCurrentWeekEmpty}
                  />
                </ViewPanel>
              )
            )}
          </>
        </PageLayouts.EntityViewLayout>
      )}
    />
  );
};
export default TimeReportingDetailsView;
